// ============================================================================
// REQ-2026-0007 — Highcharts overlay builders for the Triaxial Analysis
// range-typed picker (Max / Peak / End / Residual).
//
// Two builders:
//   - buildSlotPlotBands  → translucent vertical bands across [from, to] in
//                           εₐ coords, rendered on every strain-x chart.
//   - buildSlotMarkers    → marker dots + slot-name labels at the resolved
//                           aggregation row, returned as Highcharts scatter
//                           series. Y value is chart-specific (caller selects
//                           the snapshot field).
//
// Stress-path (t vs s' / q vs p') uses buildSlotMarkers with the snapshot
// field pair (e.g. s, t or p', q) — bands are not applicable on a parametric
// curve.
// ============================================================================

import type Highcharts from 'highcharts';
import {
  TRIAXIAL_SLOT_COLORS,
  TRIAXIAL_SLOT_LABEL,
  TRIAXIAL_SLOT_ORDER,
  bandColorFor,
  type TriaxialSlot,
} from './triaxialPickerColors';
import type { SlotRange, SlotSnapshot } from './triaxialPickerResolver';

export interface SlotState {
  slot: TriaxialSlot;
  range: SlotRange;
  snapshot: SlotSnapshot;
}

/**
 * Build vertical plotBands for slots that have both range bounds set.
 * Slot with only one bound (point-pick) is rendered as a thin plotLine.
 */
export function buildSlotPlotBands(slots: SlotState[]): Highcharts.XAxisPlotBandsOptions[] {
  const bands: Highcharts.XAxisPlotBandsOptions[] = [];
  for (const slot of TRIAXIAL_SLOT_ORDER) {
    const s = slots.find((x) => x.slot === slot);
    if (!s) continue;
    const f = s.range.fromPct;
    const t = s.range.toPct;
    if (f === null && t === null) continue;
    // For point-pick, skip the band; the marker is enough.
    if (f === null || t === null) continue;
    const lo = Math.min(f, t);
    const hi = Math.max(f, t);
    if (lo === hi) continue;
    bands.push({
      from: lo,
      to: hi,
      color: bandColorFor(slot),
      zIndex: 1,
      label: {
        text: TRIAXIAL_SLOT_LABEL[slot],
        rotation: 0,
        align: 'center',
        verticalAlign: 'top',
        y: 12,
        style: {
          fontSize: '10px',
          color: TRIAXIAL_SLOT_COLORS[slot],
          fontWeight: '600',
        },
      },
    });
  }
  return bands;
}

/** Optional plotLine at the from-only point-pick value (axial strain). */
export function buildPointPickPlotLines(slots: SlotState[]): Highcharts.XAxisPlotLinesOptions[] {
  const lines: Highcharts.XAxisPlotLinesOptions[] = [];
  for (const slot of TRIAXIAL_SLOT_ORDER) {
    const s = slots.find((x) => x.slot === slot);
    if (!s) continue;
    const f = s.range.fromPct;
    const t = s.range.toPct;
    // Point-pick only when exactly one bound is set
    const isPointPick = (f === null) !== (t === null);
    if (!isPointPick) continue;
    const value = (f ?? t) as number;
    lines.push({
      value,
      color: TRIAXIAL_SLOT_COLORS[slot],
      dashStyle: 'Dash' as Highcharts.DashStyleValue,
      width: 1,
      zIndex: 1,
      label: {
        text: TRIAXIAL_SLOT_LABEL[slot],
        align: 'center',
        verticalAlign: 'top',
        y: 12,
        style: { fontSize: '10px', color: TRIAXIAL_SLOT_COLORS[slot], fontWeight: '600' },
      },
    });
  }
  return lines;
}

type SnapshotXSelector = (s: SlotSnapshot) => number | null;
type SnapshotYSelector = (s: SlotSnapshot) => number | null;

/**
 * Build marker scatter series for each slot whose snapshot has resolved
 * values for both x and y. Each marker has a label of the form
 *   `Max (εₐ = 1.05%, q = 168 kPa)`
 * over a small white pill background for readability over chart gridlines.
 */
export function buildSlotMarkers(
  slots: SlotState[],
  pickX: SnapshotXSelector,
  pickY: SnapshotYSelector,
  labelFormatX: (v: number) => string,
  labelFormatY: (v: number) => string,
  xUnit: string,
  yUnit: string,
): Highcharts.SeriesOptionsType[] {
  const series: Highcharts.SeriesOptionsType[] = [];
  for (const slot of TRIAXIAL_SLOT_ORDER) {
    const s = slots.find((x) => x.slot === slot);
    if (!s) continue;
    const xv = pickX(s.snapshot);
    const yv = pickY(s.snapshot);
    if (xv === null || yv === null) continue;
    const color = TRIAXIAL_SLOT_COLORS[slot];
    const labelText = `${TRIAXIAL_SLOT_LABEL[slot]} (${labelFormatX(xv)}${xUnit}, ${labelFormatY(yv)}${yUnit})`;
    series.push({
      type: 'scatter',
      name: `${TRIAXIAL_SLOT_LABEL[slot]} marker`,
      data: [{ x: xv, y: yv, name: labelText }],
      color,
      marker: {
        symbol: 'circle',
        radius: 6,
        fillColor: color,
        lineColor: '#FFFFFF',
        lineWidth: 1.5,
      },
      dataLabels: {
        enabled: true,
        formatter: function (this: { point: { name?: string } }) {
          return this.point.name ?? '';
        },
        align: 'left',
        verticalAlign: 'middle',
        x: 10,
        y: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        borderColor: color,
        borderWidth: 1,
        borderRadius: 3,
        padding: 3,
        style: {
          fontSize: '10px',
          fontWeight: '600',
          color: color,
          textOutline: 'none',
        },
      },
      enableMouseTracking: true,
      tooltip: {
        headerFormat: `<b>${TRIAXIAL_SLOT_LABEL[slot]}</b><br/>`,
        pointFormat: '{point.name}',
      },
      zIndex: 10,
      showInLegend: false,
    });
  }
  return series;
}
